import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';
import useAuthStore from '../../store/authStore';
import { fetchParentJournalPreviews } from '../../api/students';
import { SUBJECT_OPTIONS } from '../../components/forms/StudentFormDialog';

dayjs.locale('uk');

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', bgcolor: '#fff' };
const accentColors = ['#4357AD', '#22C55E', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#EF4444', '#14B8A6'];

const formatNextLesson = (startTime) => {
  const d = dayjs(startTime);
  const time = d.format('HH:mm');
  if (d.isSame(dayjs(), 'day')) return `сьогодні · ${time}`;
  if (d.isSame(dayjs().add(1, 'day'), 'day')) return `завтра · ${time}`;
  return `${d.format('dd, DD.MM')} · ${time}`;
};

const InfoRow = ({ label, children }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
    <Typography variant="body2" sx={{ color: '#8891A5', flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ minWidth: 0, textAlign: 'right' }}>{children}</Box>
  </Stack>
);

const PaymentStatusChip = ({ preview }) => {
  const hasDebt = preview.debtLessons > 0;
  const style = hasDebt
    ? { label: `Борг ${preview.debtLessons}`, bg: '#FEF3C7', color: '#92400E' }
    : { label: 'Сплачено', bg: '#D1FAE5', color: '#065F46' };
  const tooltip = hasDebt
    ? `Неоплачених занять: ${preview.debtLessons}`
    : preview.remainingCredits > 0
      ? `Передплачених занять: ${preview.remainingCredits}`
      : 'Усі заняття оплачено';
  return (
    <Tooltip title={tooltip} arrow>
      <Box
        sx={{
          px: 1.5,
          py: 0.4,
          borderRadius: 2,
          fontSize: 12,
          fontWeight: 700,
          bgcolor: style.bg,
          color: style.color,
          flexShrink: 0,
          cursor: 'default',
        }}
      >
        {style.label}
      </Box>
    </Tooltip>
  );
};

const ScoreValue = ({ preview }) => {
  const { averageScore, scoreDelta } = preview;
  if (averageScore === null || averageScore === undefined) {
    return <Typography variant="body2" sx={{ color: '#C4B5FD', fontStyle: 'italic' }}>немає оцінок</Typography>;
  }
  const deltaColor = scoreDelta > 0 ? '#16A34A' : scoreDelta < 0 ? '#DC2626' : '#8891A5';
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
      <SignalCellularAltIcon sx={{ fontSize: 18, color: '#C4B5FD' }} />
      <Typography variant="body2" fontWeight={800} sx={{ color: B.dark }}>
        {averageScore.toFixed(1)}
      </Typography>
      {scoreDelta !== null && scoreDelta !== undefined && (
        <Tooltip title="Порівняно з попереднім місяцем" arrow>
          <Typography variant="caption" fontWeight={700} sx={{ color: deltaColor, cursor: 'default' }}>
            {scoreDelta > 0 ? '+' : ''}{scoreDelta.toFixed(1)}
          </Typography>
        </Tooltip>
      )}
    </Stack>
  );
};

const JournalPreviewCard = ({ preview, color }) => {
  const navigate = useNavigate();
  const subjectLabel = SUBJECT_OPTIONS.find((o) => o.value === preview.subjects)?.label || preview.subjects;

  return (
    <Card
      sx={{
        ...cardSx,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderTop: `4px solid ${color}`,
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': { boxShadow: '0 4px 20px rgba(67,87,173,0.12)', transform: 'translateY(-2px)' },
      }}
    >
      <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', flex: 1, '&:last-child': { pb: 2.5 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar sx={{ width: 48, height: 48, bgcolor: `${color}18`, color, fontWeight: 700, fontSize: 18 }}>
            {preview.studentName?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ color: B.dark, lineHeight: 1.3 }} noWrap>
              {preview.studentName}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
              {preview.grade && (
                <Chip
                  icon={<SchoolIcon sx={{ fontSize: 14 }} />}
                  label={preview.grade}
                  size="small"
                  sx={{ bgcolor: '#EDE9FE', color: B.primary, fontWeight: 600, fontSize: 12, height: 24, '& .MuiChip-icon': { color: B.primary } }}
                />
              )}
              {subjectLabel && (
                <Typography variant="caption" sx={{ color: '#8891A5' }} noWrap>{subjectLabel}</Typography>
              )}
              {preview.archived && (
                <Chip
                  icon={<ArchiveOutlinedIcon sx={{ fontSize: 14 }} />}
                  label="Архів"
                  size="small"
                  sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 700, fontSize: 11, height: 22, '& .MuiChip-icon': { color: '#92400E' } }}
                />
              )}
            </Stack>
          </Box>
          <PaymentStatusChip preview={preview} />
        </Stack>

        <Divider sx={{ my: 2, borderColor: '#F1F0FB' }} />

        <Stack spacing={1.25} sx={{ flex: 1 }}>
          <InfoRow label="Наступне заняття">
            {preview.nextLessonAt ? (
              <Typography variant="body2" fontWeight={800} sx={{ color: B.dark }}>
                {formatNextLesson(preview.nextLessonAt)}
              </Typography>
            ) : (
              <Typography variant="body2" fontWeight={700} sx={{ color: '#F59E0B' }}>
                не заплановано
              </Typography>
            )}
          </InfoRow>
          <InfoRow label="Репетитор">
            <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }} noWrap>
              {preview.tutorName || '—'}
            </Typography>
          </InfoRow>
          <InfoRow label="Середній бал">
            <ScoreValue preview={preview} />
          </InfoRow>
        </Stack>

        <Button
          fullWidth
          variant="contained"
          disableElevation
          onClick={() => navigate(`/parent/journal/${preview.studentId}`)}
          sx={{
            mt: 2.5,
            bgcolor: B.primary,
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 700,
            py: 1.1,
            '&:hover': { bgcolor: '#3648A0' },
          }}
        >
          Відкрити журнал
        </Button>
      </CardContent>
    </Card>
  );
};

const ParentStudentsPage = () => {
  const { user } = useAuthStore();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['parent-journal-previews', user?.id],
    queryFn: () => fetchParentJournalPreviews(user.id),
    enabled: Boolean(user?.id),
  });

  const previews = data || [];

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: B.primary }} /></Box>;
  }

  if (isError) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4" fontWeight={800} sx={{ color: B.dark }}>Журнали</Typography>
        <Card sx={cardSx}>
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: B.dark, mb: 0.5 }}>Не вдалося завантажити журнали</Typography>
            <Typography variant="body2" sx={{ color: '#8891A5' }}>Спробуйте оновити сторінку.</Typography>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  if (previews.length === 0) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4" fontWeight={800} sx={{ color: B.dark }}>Журнали</Typography>
        <Card sx={cardSx}>
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: B.dark, mb: 0.5 }}>Журналів поки немає</Typography>
            <Typography variant="body2" sx={{ color: '#8891A5' }}>Зверніться до адміністратора.</Typography>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h4" fontWeight={800} sx={{ color: B.dark }}>Журнали</Typography>
        <Typography variant="body1" sx={{ color: '#8891A5', mt: 0.5 }}>
          Оберіть журнал для перегляду
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {previews.map((preview, i) => (
          <Grid item xs={12} sm={6} md={4} key={preview.studentId}>
            <JournalPreviewCard preview={preview} color={accentColors[i % accentColors.length]} />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};

export default ParentStudentsPage;
